import { ViewSuperblocksV2Provider } from "contexts/ViewSuperblocksV2Context";
import { memo, Suspense, lazy, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Navigate,
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useRouteError,
  createMemoryRouter,
  redirect,
} from "react-router-dom";

import {
  AccessMode,
  IntegrationKind,
  isEnterpriseLikePlan,
  isPolicyGatesEntitledPlan,
  OAUTH_CALLBACK_PATH,
  OrgFeature,
} from "@superblocksteam/shared";

import Auth0ProviderWithHistory from "auth/authProvider";
import AuthWrapper from "auth/authWrapper";
import { LoginComponent, LogoutComponent, SignupComponent } from "auth/utils";
import { AiQuotaPaywallModal } from "components/AiQuotaPaywallModal";
import EmailVerificationWrapper from "components/app/EmailVerification/EmailVerificationWrapper";
import { IntercomOverlay } from "components/app/Intercom/IntercomOverlay";
import { FullPageSpinner } from "components/ui/FullPageSpinner";
import {
  VIEW_ACCESS_TOKENS,
  VIEW_AGENTS,
  VIEW_GROUPS,
  VIEW_OBSERVABILITY,
  VIEW_PROFILES,
  VIEW_REPOSITORIES,
  VIEW_ROLES,
  VIEW_SECRET_STORES,
  VIEW_USERS,
  VIEW_GROUP_MEMBERS,
  MANAGE_AI_INFERENCE,
  MANAGE_GROUPS,
  MANAGE_APP_DATABASES,
  MANAGE_NPM_REGISTRY,
  MANAGE_POLICIES,
  READ_ORGANIZATION,
  MANAGE_BILLING,
  VIEW_POLICIES,
} from "constants/rbac";
import CreditCardGateWrapper from "core/components/CreditCardGate/CreditCardGateWrapper";
import JoinOrgGateWrapper from "core/components/JoinOrgGate/JoinOrgGateWrapper";
import { isCloudPrem } from "core/utils/deployment";
import { JwtContextProvider } from "hooks/ui/JwtProvider";
import { useFeatureFlag } from "hooks/ui/useFeatureFlag";
import { useOrgFeature } from "hooks/ui/useOrgFeature";
import { closeAiQuotaPaywallModal } from "legacy/actions/modalActions";
import { ApplicationRoute } from "legacy/constants/routes";
import ErrorPage from "legacy/pages/common/ErrorPage";
import PageLoadingBar from "legacy/pages/common/PageLoadingSpinner";
import PageNotFound from "legacy/pages/common/PageNotFound";
import {
  getSafeCrash,
  getSafeCrashCode,
  getSafeCrashReason,
  getSafeCrashReauth,
  getSafeCrashSignInRefusal,
} from "legacy/selectors/errorSelectors";
import { getModalState } from "legacy/selectors/modalSelectors";
import { getAccessMode } from "legacy/selectors/usersSelectors";
import { AuthorizedPageWrapper } from "pages/AccessTokens/AuthorizedPageWrapper";
import OAuthImplicitCallback from "pages/components/OAuthCallback";
import { MANAGE_CUSTOM_AGENT_MODELS } from "pages/CustomAgentModels/permissions";
import { RoleAndPermissionsPages } from "pages/Permissions/constants";
import {
  POLICY_GATES_DEMO_URL,
  POLICIES_URL,
  ROLES_AND_PERMISSIONS_URL,
} from "pages/routes";
import { SecurityCenterAuthorizedPageWrapper } from "pages/SecurityCenter/SecurityCenterAuthorizedPageWrapper";
import { getStore } from "store/dynamic";
import { useAppDispatch, useAppSelector } from "store/helpers";
import { Flag, selectFlagById } from "store/slices/featureFlags";
import { selectOnlyOrganization } from "store/slices/organizations";
import { useTracingManager } from "tracing/hooks";
import { isDev } from "utils/env";
import { isManagedNativeDatabasesAvailable } from "utils/managedNativeDatabases";

import { AdminDemoPagesGuard } from "./AdminDemoPagesGuard";
import { ApiProfileReloader } from "./ApiProfileReloader";
import { SuperuserPageWrapper } from "./pages/AccessTokens/SuperUserPageWrapper";
import { AdminDemoProvider } from "./pages/AdminDemo/AdminDemoProvider";

const loadingIndicator = <PageLoadingBar />;

const LazyDowngradeModal = lazy(
  () => import(/* webpackChunkName: "modals" */ "pages/Home/DowngradeModal"),
);
const LazyPricingModal = lazy(
  () => import(/* webpackChunkName: "modals" */ "pages/Home/PricingModal"),
);
const LazyPurchasedModal = lazy(
  () => import(/* webpackChunkName: "modals" */ "pages/Home/PurchasedModal"),
);
const LazyQualificationModal = lazy(
  () =>
    import(/* webpackChunkName: "modals" */ "pages/Home/QualificationModal"),
);

function OrgFeatureGuard({
  feature,
  children,
  fallback,
}: {
  feature: OrgFeature;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const hasFeature = useOrgFeature(feature);
  return hasFeature ? <>{children}</> : <>{fallback ?? <PageNotFound />}</>;
}

function FeatureFlagGuard({
  flag,
  orFlag,
  children,
}: {
  flag: Flag;
  orFlag?: Flag;
  children: React.ReactNode;
}) {
  const enabled = useFeatureFlag(flag);
  const orEnabled = useFeatureFlag(orFlag ?? flag);
  return enabled || orEnabled ? <>{children}</> : <PageNotFound />;
}

// Hybrid gate for the Secrets Management page. V1 orgs see it via the legacy
// org-feature gate; V2 orgs see it when the LD flag is on for the org so they
// can manage integration-level secrets via AWS Secrets Manager (APPS-3877).
function SecretsManagementGuard({ children }: { children: React.ReactNode }) {
  const hasLegacy = useOrgFeature(OrgFeature.LegacySecretsManagement);
  const v2Enabled = useFeatureFlag(Flag.SECRETS_MANAGEMENT_V2_ENABLED);
  return hasLegacy || v2Enabled ? <>{children}</> : <PageNotFound />;
}

function ManagedNativeDatabasesGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  const managedNativeDbEnabled = useFeatureFlag(Flag.MANAGED_NATIVE_DB_ENABLED);
  const organization = useSelector(selectOnlyOrganization);
  return isManagedNativeDatabasesAvailable({
    managedNativeDbEnabled,
    organization,
    isCloudPrem: isCloudPrem(),
  }) ? (
    <>{children}</>
  ) : (
    <PageNotFound />
  );
}

function EnterpriseOnlyRouteGuard({ children }: { children: React.ReactNode }) {
  const organization = useSelector(selectOnlyOrganization);
  const canAccess = isEnterpriseLikePlan(organization?.billing?.plan);

  return canAccess ? <>{children}</> : <Navigate to="/home" replace />;
}

// Policy Gates MVP is gated behind the POLICY_GATES_MVP_ENABLED flag AND
// restricted to entitled (enterprise/POC) orgs. The server enforces the same
// rule, so this guard only hides the admin surfaces for non-entitled orgs.
function PoliciesGuard({ children }: { children: React.ReactNode }) {
  const enabled = useFeatureFlag(Flag.POLICY_GATES_MVP_ENABLED);
  const organization = useSelector(selectOnlyOrganization);
  const entitled = isPolicyGatesEntitledPlan(organization?.billing?.plan);

  return enabled && entitled ? <>{children}</> : <PageNotFound />;
}

function PrivateRoutes() {
  return (
    <AuthWrapper>
      <ViewSuperblocksV2Provider>
        <JwtContextProvider>
          <EmailVerificationWrapper>
            <JoinOrgGateWrapper>
              <CreditCardGateWrapper>
                <Outlet />
              </CreditCardGateWrapper>
            </JoinOrgGateWrapper>
          </EmailVerificationWrapper>
        </JwtContextProvider>
      </ViewSuperblocksV2Provider>
    </AuthWrapper>
  );
}

const MemoizedPrivateRoutes = memo(PrivateRoutes);

async function lazyHome() {
  const { default: Home } = await import(
    /* webpackChunkName: "home" */ "pages/Home"
  );
  return {
    element: (
      <IntercomOverlay>
        <Home />
      </IntercomOverlay>
    ),
  };
}

async function lazyCustomAgents() {
  const { default: CustomAgentsPage } = await import("pages/CustomAgents");
  return {
    element: (
      <FeatureFlagGuard flag={Flag.CUSTOM_AGENTS_ENABLED}>
        <CustomAgentsPage />
      </FeatureFlagGuard>
    ),
  };
}

async function lazyCustomAgentModels() {
  const { default: CustomAgentModels } =
    await import("pages/CustomAgentModels");
  return {
    element: (
      <FeatureFlagGuard flag={Flag.CUSTOM_AGENTS_ENABLED}>
        <AuthorizedPageWrapper permissions={[MANAGE_CUSTOM_AGENT_MODELS]}>
          <CustomAgentModels />
        </AuthorizedPageWrapper>
      </FeatureFlagGuard>
    ),
  };
}

async function lazyCreateFromPrompt() {
  const { default: CreateFromPrompt } = await import(
    /* webpackChunkName: "create-from-prompt" */ "pages/CreateFromPrompt"
  );
  return { element: <CreateFromPrompt /> };
}

async function lazyIntegrations() {
  const { default: Integrations } = await import(
    /* webpackChunkName: "integrations" */ "pages/Integrations"
  );
  return {
    element: (
      <IntercomOverlay>
        <Integrations />
      </IntercomOverlay>
    ),
  };
}

async function lazyIntegrationForm(kind: IntegrationKind) {
  const { IntegrationForm } = await import(
    /* webpackChunkName: "integration-form" */ "pages/Integrations/IntegrationForm"
  );
  return {
    element: (
      <IntercomOverlay>
        <IntegrationForm kind={kind} />
      </IntercomOverlay>
    ),
  };
}

async function lazyProfiles() {
  const { default: Profiles } = await import(
    /* webpackChunkName: "profiles" */ "pages/Profiles"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_PROFILES]}>
          <Profiles />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyEnvironments() {
  const { default: Environments } = await import(
    /* webpackChunkName: "environments" */ "pages/Environments/Environments"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_PROFILES]}>
          <Environments />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

// Data tags and data planes share one page. Anyone who can view either gets
// in; the page hides the section they cannot see and handles the Enterprise
// gate for data planes itself.
async function lazyDataSetup() {
  const { default: DataSetup } = await import(
    /* webpackChunkName: "data-setup" */ "pages/DataSetup/DataSetup"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_PROFILES, VIEW_AGENTS]}>
          <DataSetup />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyNativeDatabases() {
  const { default: NativeDatabases } = await import(
    /* webpackChunkName: "native-databases" */ "pages/NativeDatabases"
  );
  return {
    element: (
      <IntercomOverlay>
        <ManagedNativeDatabasesGuard>
          <AuthorizedPageWrapper permissions={[MANAGE_APP_DATABASES]}>
            <NativeDatabases />
          </AuthorizedPageWrapper>
        </ManagedNativeDatabasesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyRepositories() {
  const { default: Repositories } = await import(
    /* webpackChunkName: "repositories" */ "pages/Repositories"
  );
  return {
    element: (
      <OrgFeatureGuard feature={OrgFeature.LegacySourceControl}>
        <IntercomOverlay>
          <AuthorizedPageWrapper permissions={[VIEW_REPOSITORIES]}>
            <Repositories />
          </AuthorizedPageWrapper>
        </IntercomOverlay>
      </OrgFeatureGuard>
    ),
  };
}

async function lazyRepositoryDetails() {
  const { default: RepositoryDetails } = await import(
    /* webpackChunkName: "repository-details" */ "pages/Repositories/RepositoryDetails"
  );
  return {
    element: (
      <OrgFeatureGuard feature={OrgFeature.LegacySourceControl}>
        <IntercomOverlay>
          <RepositoryDetails />
        </IntercomOverlay>
      </OrgFeatureGuard>
    ),
  };
}

async function lazyAgents() {
  const { default: Agents } = await import(
    /* webpackChunkName: "agents" */ "pages/Agents"
  );
  return {
    element: (
      <IntercomOverlay>
        <EnterpriseOnlyRouteGuard>
          <AuthorizedPageWrapper permissions={[VIEW_AGENTS]}>
            <Agents />
          </AuthorizedPageWrapper>
        </EnterpriseOnlyRouteGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyAgentDeploymentInstructionPage() {
  const { default: AgentDeploymentInstructionPage } = await import(
    /* webpackChunkName: "agent-deployment" */ "pages/Agents/AgentDeploymentInstructionPage"
  );
  const { OpasInstructionsGuard } =
    await import("pages/Agents/OpasInstructionsGuard");
  return {
    element: (
      <IntercomOverlay>
        <EnterpriseOnlyRouteGuard>
          <AuthorizedPageWrapper permissions={[VIEW_AGENTS]}>
            <OpasInstructionsGuard>
              <AgentDeploymentInstructionPage />
            </OpasInstructionsGuard>
          </AuthorizedPageWrapper>
        </EnterpriseOnlyRouteGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyDataSetupInstructionPage() {
  const { default: AgentDeploymentInstructionPage } = await import(
    /* webpackChunkName: "agent-deployment" */ "pages/Agents/AgentDeploymentInstructionPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <EnterpriseOnlyRouteGuard>
          <AuthorizedPageWrapper permissions={[VIEW_AGENTS]}>
            <AgentDeploymentInstructionPage />
          </AuthorizedPageWrapper>
        </EnterpriseOnlyRouteGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyAccessTokensPage() {
  const { default: AccessTokensPage } = await import(
    /* webpackChunkName: "access-tokens" */ "pages/AccessTokens"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_ACCESS_TOKENS]}>
          <AccessTokensPage />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

let knowledgeComponents: typeof import("pages/ClarkKnowledge/routes") | null =
  null;

async function loadKnowledgeComponents() {
  if (!knowledgeComponents) {
    knowledgeComponents = await import(
      /* webpackChunkName: "clark-knowledge" */ "pages/ClarkKnowledge/routes"
    );
  }
  return knowledgeComponents;
}

async function lazyKnowledgeLayout() {
  const { KnowledgeLayout } = await loadKnowledgeComponents();
  return {
    element: (
      <OrgFeatureGuard
        feature={OrgFeature.Knowledge}
        fallback={<AccessModeRedirect />}
      >
        <IntercomOverlay>
          <KnowledgeLayout />
        </IntercomOverlay>
      </OrgFeatureGuard>
    ),
  };
}

async function lazyKnowledgeList() {
  const { KnowledgeListPage } = await loadKnowledgeComponents();
  return { element: <KnowledgeListPage /> };
}

async function lazyKnowledgeCreate() {
  const { KnowledgeCreate } = await loadKnowledgeComponents();
  return { element: <KnowledgeCreate /> };
}

async function lazyKnowledgeDetail() {
  const { KnowledgeDetail } = await loadKnowledgeComponents();
  return { element: <KnowledgeDetail /> };
}

async function lazyDatabasePage() {
  const { DatabasePage } = await import(
    /* webpackChunkName: "database" */ "pages/Database/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard flag={Flag.BYO_DB_DEMO}>
          <DatabasePage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyExternalAuthPage() {
  const { ExternalAuthPage } = await import(
    /* webpackChunkName: "external-auth" */ "pages/ExternalAuth/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <ExternalAuthPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyExternalExperiencePage() {
  const { ExternalExperiencePage } = await import(
    /* webpackChunkName: "external-experience" */ "pages/ExternalExperience/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <ExternalExperiencePage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyExternalUsersPage() {
  const { ExternalUsersPage } = await import(
    /* webpackChunkName: "external-users" */ "pages/ExternalUsers/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <ExternalUsersPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyFileStoragePage() {
  const { FileStoragePage } = await import(
    /* webpackChunkName: "file-storage" */ "pages/FileStorage/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <FileStoragePage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyAppAnalyticsPage() {
  const { AppAnalyticsPage } = await import(
    /* webpackChunkName: "app-analytics" */ "pages/AppAnalytics/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <AppAnalyticsPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyObservabilityMonitorPage() {
  const { ObservabilityMonitorPage } = await import(
    /* webpackChunkName: "observability-monitor" */ "pages/ObservabilityMonitor/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <ObservabilityMonitorPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPlatformMcpPage() {
  const { PlatformMcpPage } = await import(
    /* webpackChunkName: "platform-mcp" */ "pages/PlatformMcp/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <PlatformMcpPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyOverviewPage() {
  const { OverviewPage } = await import(
    /* webpackChunkName: "overview" */ "pages/Overview/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <OverviewPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPlatformSetupPage() {
  const { PlatformSetupPage } = await import(
    /* webpackChunkName: "platform-setup" */ "pages/PlatformSetup/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <PlatformSetupPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyIdentityPage() {
  const { IdentityPage } = await import(
    /* webpackChunkName: "identity" */ "pages/Identity/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <IdentityPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyWorkflowsPage() {
  const { WorkflowsPage } = await import(
    /* webpackChunkName: "workflows" */ "pages/Workflows/routes"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <WorkflowsPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyUsersPage() {
  const { default: UsersPage } = await import(
    /* webpackChunkName: "users" */ "pages/Users"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_USERS]}>
          <UsersPage />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyUserDetail() {
  const { default: UserDetail } = await import(
    /* webpackChunkName: "user-detail" */ "pages/Users/UserDetail"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_USERS]}>
          <UserDetail />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyGroups() {
  const { default: Groups } = await import(
    /* webpackChunkName: "groups" */ "pages/Groups"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_GROUPS]}>
          <Groups />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyGroupDetail() {
  const { default: GroupDetail } = await import(
    /* webpackChunkName: "group-detail" */ "pages/Groups/GroupDetail"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper
          permissions={[VIEW_GROUP_MEMBERS, MANAGE_GROUPS]}
        >
          <GroupDetail />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyRolesAndPermissions() {
  const { default: RolesAndPermissions } = await import(
    /* webpackChunkName: "roles-permissions" */ "pages/Permissions/RolesAndPermissions"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[VIEW_ROLES]}>
          <RolesAndPermissions />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyAuditLogs() {
  const { default: AuditLogs } = await import(
    /* webpackChunkName: "audit-logs" */ "pages/AuditLogs"
  );
  return {
    element: (
      <IntercomOverlay>
        <EnterpriseOnlyRouteGuard>
          <AuditLogs />
        </EnterpriseOnlyRouteGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyObservability() {
  const { default: Observability } = await import(
    /* webpackChunkName: "observability" */ "pages/Observability"
  );
  return {
    element: (
      <OrgFeatureGuard feature={OrgFeature.LegacyObservability}>
        <IntercomOverlay>
          <AuthorizedPageWrapper permissions={[VIEW_OBSERVABILITY]}>
            <Observability />
          </AuthorizedPageWrapper>
        </IntercomOverlay>
      </OrgFeatureGuard>
    ),
  };
}

async function lazySecretsManagement() {
  const { default: SecretsManagement } = await import(
    /* webpackChunkName: "secrets-management" */ "pages/SecretsManagement"
  );
  return {
    element: (
      <SecretsManagementGuard>
        <IntercomOverlay>
          <AuthorizedPageWrapper permissions={[VIEW_SECRET_STORES]}>
            <SecretsManagement />
          </AuthorizedPageWrapper>
        </IntercomOverlay>
      </SecretsManagementGuard>
    ),
  };
}

async function lazyGeneralPage() {
  const { default: GeneralPage } = await import(
    /* webpackChunkName: "general" */ "pages/General"
  );
  return {
    element: (
      <IntercomOverlay>
        <AuthorizedPageWrapper permissions={[READ_ORGANIZATION]}>
          <GeneralPage />
        </AuthorizedPageWrapper>
      </IntercomOverlay>
    ),
  };
}

async function lazyUsagePage() {
  const { default: UsagePage } = await import(
    /* webpackChunkName: "usage" */ "pages/Usage"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.BILLING_USAGE_PAGE_ENABLED}>
          <AuthorizedPageWrapper permissions={[MANAGE_BILLING]}>
            <UsagePage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyAiInferencePage() {
  const { default: AiInferencePage } = await import(
    /* webpackChunkName: "ai-inference" */ "pages/AiInference"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.AI_BYO_INFERENCE_ENABLED}>
          <AuthorizedPageWrapper permissions={[MANAGE_AI_INFERENCE]}>
            <AiInferencePage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

// Per APPS-4229: per-org Package Registry settings page. Gated on
// `superblocks.npm-registry.enabled` (per-org LD flag from APPS-4228) and on
// `npm_registry.manage` (Admin + Owner) so non-admin members can't even open
// the form. APPS-4517 moved this off `org.manage` (Owner-only) onto the
// dedicated permission so org admins — not just the Owner — can manage
// registries. Mounted under `/package-registry` to keep the URL stable across
// cycles where the `npm-registry` server route owns the API path.
async function lazyPackageRegistryPage() {
  const { default: PackageRegistryPage } = await import(
    /* webpackChunkName: "package-registry" */ "pages/PackageRegistry"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.NPM_REGISTRY_ENABLED}>
          <AuthorizedPageWrapper permissions={[MANAGE_NPM_REGISTRY]}>
            <PackageRegistryPage />
          </AuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazySpendingPage() {
  const { default: SpendingPage } = await import(
    /* webpackChunkName: "spending" */ "pages/Spending"
  );
  return {
    element: (
      <IntercomOverlay>
        {/* Gated on its own flag (not BILLING_USAGE_PAGE_ENABLED) so ops can
            roll out /spending independently of /usage. */}
        <FeatureFlagGuard
          flag={Flag.BILLING_SPENDING_PAGE_ENABLED}
          orFlag={Flag.NAV_REDESIGN_ENABLED}
        >
          <SpendingPage />
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPersonalSettings() {
  const { default: PersonalSettings } = await import(
    /* webpackChunkName: "personal-settings" */ "pages/PersonalSettings"
  );
  return {
    element: (
      <IntercomOverlay>
        <PersonalSettings />
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyGatesDemo() {
  const { default: PolicyGatesDemo } = await import(
    /* webpackChunkName: "policy-gates-demo" */ "pages/PolicyGatesDemo"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard flag={Flag.ADMIN_DEMO_POLICY_AGENTS_ENABLED}>
          <PolicyGatesDemo />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyGatesDemoDetail() {
  const { default: PolicyGateDetailPage } = await import(
    /* webpackChunkName: "policy-gates-demo" */ "pages/PolicyGatesDemo/PolicyGateDetailPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard flag={Flag.ADMIN_DEMO_POLICY_AGENTS_ENABLED}>
          <PolicyGateDetailPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazySecurityCenterDemo() {
  const { default: SecurityCenterDemo } = await import(
    /* webpackChunkName: "security-center-demo" */ "pages/SecurityCenterDemo"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <SecurityCenterDemo />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazySecurityCenter() {
  const { default: SecurityCenter } = await import(
    /* webpackChunkName: "security-center" */ "pages/SecurityCenter"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.CODE_SECURITY_DEPENDENCY_SCAN_ENABLED}>
          <SecurityCenterAuthorizedPageWrapper>
            <SecurityCenter />
          </SecurityCenterAuthorizedPageWrapper>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazySecurityCenterApplication() {
  const { default: ApplicationSbomPage } = await import(
    /* webpackChunkName: "security-center" */ "pages/SecurityCenter/ApplicationSbomPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.CODE_SECURITY_DEPENDENCY_SCAN_ENABLED}>
          <FeatureFlagGuard flag={Flag.CODE_SECURITY_SBOM_ENABLED}>
            <SecurityCenterAuthorizedPageWrapper>
              <ApplicationSbomPage />
            </SecurityCenterAuthorizedPageWrapper>
          </FeatureFlagGuard>
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyCodeSecurityReview() {
  const { default: CodeSecurityReviewPage } = await import(
    /* webpackChunkName: "security-center-demo" */ "pages/SecurityCenterDemo/CodeSecurityReviewPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <AdminDemoPagesGuard>
          <CodeSecurityReviewPage />
        </AdminDemoPagesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyGatesDemoReset() {
  const { resetPolicyGatesDemoStorage } = await import(
    /* webpackChunkName: "policy-gates-demo" */ "pages/PolicyGatesDemo/policyGateCreatedGateStore"
  );
  return {
    loader: () => {
      // The reset utility belongs to the removable stakeholder demo; only run
      // it (and bounce to the demo) when the demo page is actually reachable.
      // The demo page sits behind AdminDemoPagesGuard, which requires BOTH the
      // nav redesign and policy-agents demo flags, so mirror that here.
      // Otherwise treat the route as nonexistent for the org.
      const state = getStore().getState();
      const demoReachable =
        selectFlagById(state, Flag.NAV_REDESIGN_ENABLED) &&
        selectFlagById(state, Flag.ADMIN_DEMO_POLICY_AGENTS_ENABLED);
      if (!demoReachable) {
        return redirect("/home");
      }
      resetPolicyGatesDemoStorage();
      window.location.replace(POLICY_GATES_DEMO_URL);
      return redirect(POLICY_GATES_DEMO_URL);
    },
  };
}

async function lazyAppAiPage() {
  const { default: AppAiPage } = await import(
    /* webpackChunkName: "app-ai" */ "pages/AppAi"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard
          flag={Flag.APP_AI_DEFAULT_INTEGRATION_ENABLED}
          orFlag={Flag.NAV_REDESIGN_ENABLED}
        >
          <AppAiPage />
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPoliciesPage() {
  const { default: PoliciesPage } = await import(
    /* webpackChunkName: "policies" */ "pages/Policies"
  );
  return {
    element: (
      <IntercomOverlay>
        <PoliciesGuard>
          <AuthorizedPageWrapper permissions={[VIEW_POLICIES]}>
            <PoliciesPage />
          </AuthorizedPageWrapper>
        </PoliciesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyRunsPage() {
  const { default: PolicyRunsPage } = await import(
    /* webpackChunkName: "policies" */ "pages/Policies/PolicyRunsPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <PoliciesGuard>
          <AuthorizedPageWrapper permissions={[VIEW_POLICIES]}>
            <PolicyRunsPage />
          </AuthorizedPageWrapper>
        </PoliciesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyRunReportPage() {
  const { default: PolicyRunReportPage } = await import(
    /* webpackChunkName: "policies" */ "pages/Policies/PolicyRunReportPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <PoliciesGuard>
          <AuthorizedPageWrapper permissions={[VIEW_POLICIES]}>
            <PolicyRunReportPage />
          </AuthorizedPageWrapper>
        </PoliciesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyPolicyAgentEditPage() {
  const { default: PolicyRunsPage } = await import(
    /* webpackChunkName: "policies" */ "pages/Policies/PolicyRunsPage"
  );
  return {
    element: (
      <IntercomOverlay>
        <PoliciesGuard>
          <AuthorizedPageWrapper permissions={[VIEW_POLICIES]}>
            <AuthorizedPageWrapper permissions={[MANAGE_POLICIES]}>
              <PolicyRunsPage />
            </AuthorizedPageWrapper>
          </AuthorizedPageWrapper>
        </PoliciesGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyInboxPage() {
  const { default: InboxPage } = await import(
    /* webpackChunkName: "inbox" */ "pages/Inbox"
  );
  return {
    element: (
      <IntercomOverlay>
        <FeatureFlagGuard flag={Flag.NOTIFICATIONS_INBOX_ENABLED}>
          <InboxPage />
        </FeatureFlagGuard>
      </IntercomOverlay>
    ),
  };
}

async function lazyCheckoutLoader() {
  const { default: CheckoutLoader } = await import(
    /* webpackChunkName: "checkout" */ "pages/Checkout/loader"
  );
  return {
    element: (
      <IntercomOverlay>
        <CheckoutLoader />
      </IntercomOverlay>
    ),
  };
}

async function lazyAcceptInvite() {
  const { default: AcceptInvitePage } = await import(
    /* webpackChunkName: "accept-invite" */ "pages/AcceptInvite"
  );
  return { element: <AcceptInvitePage /> };
}

async function lazyCheckoutCompletePage() {
  const { default: CheckoutCompletePage } = await import(
    /* webpackChunkName: "checkout-complete" */ "pages/CheckoutComplete"
  );
  return {
    element: (
      <IntercomOverlay>
        <CheckoutCompletePage />
      </IntercomOverlay>
    ),
  };
}

async function lazyDBFSDownloadPage() {
  const { default: DBFSDownloadPage } = await import(
    /* webpackChunkName: "dbfs-download" */ "pages/DBFSDownload"
  );
  return {
    element: (
      <SuperuserPageWrapper>
        <DBFSDownloadPage />
      </SuperuserPageWrapper>
    ),
  };
}

async function lazyGitHubOAuthCallback() {
  const { default: GitHubOAuthCallback } = await import(
    /* webpackChunkName: "github-oauth" */ "pages/components/GithubOAuthCallback"
  );
  return { element: <GitHubOAuthCallback /> };
}

function AccessModeRedirect() {
  const accessMode = useSelector(getAccessMode);
  if (accessMode === AccessMode.VISITOR) {
    return <Navigate to={"/login"} />;
  }
  return <Navigate to={"/home"} />;
}

function HardRedirect() {
  const search = window.location.search;
  if (search.includes("code=") && search.includes("state=")) {
    return null;
  }
  window.location.replace(window.location.href);
  return null;
}

function GlobalAiQuotaPaywallModal() {
  const modalState = useAppSelector(getModalState);
  const dispatch = useAppDispatch();

  const onClose = useCallback(() => {
    dispatch(closeAiQuotaPaywallModal());
  }, [dispatch]);

  return (
    <AiQuotaPaywallModal
      open={modalState.aiQuotaPaywallReason !== null}
      reason={modalState.aiQuotaPaywallReason ?? undefined}
      onClose={onClose}
    />
  );
}

function TopLevelRoutes() {
  useTracingManager();

  const safeCrash = useSelector(getSafeCrash);
  const safeCrashCode = useSelector(getSafeCrashCode);
  const safeCrashReason = useSelector(getSafeCrashReason);
  const safeCrashReauth = useSelector(getSafeCrashReauth);
  const safeCrashSignInRefusal = useSelector(getSafeCrashSignInRefusal);

  if (safeCrash && safeCrashCode) {
    return (
      <Auth0ProviderWithHistory>
        <ErrorPage
          code={safeCrashCode}
          reason={safeCrashReason}
          reauth={safeCrashReauth}
          signInRefusal={safeCrashSignInRefusal}
        />
      </Auth0ProviderWithHistory>
    );
  }

  return (
    <Auth0ProviderWithHistory>
      <Outlet />

      {/* These can appear on any page. The local Suspense keeps their chunk
          loads from suspending the whole router into the top-level fallback,
          whose bootstrap stage report ("route") would otherwise land before
          auth and pin the monotonic progress bar past the auth milestones. */}
      <Suspense fallback={null}>
        <LazyDowngradeModal />
        <LazyPricingModal />
        <GlobalAiQuotaPaywallModal />
        <LazyPurchasedModal />
        <LazyQualificationModal />
      </Suspense>

      {/* TODO: Remove this once control flow is fully released */}
      <ApiProfileReloader />
    </Auth0ProviderWithHistory>
  );
}

// See https://github.com/remix-run/react-router/discussions/10166
const BubbleError = () => {
  const error = useRouteError();
  throw error;
};

// detect if running in test environment because router depends on some browser apis
export const router = (
  process.env.NODE_ENV === "test" ? createMemoryRouter : createBrowserRouter
)([
  {
    element: <TopLevelRoutes />,
    errorElement: <BubbleError />,
    children: [
      { path: "/login", element: <LoginComponent /> },
      { path: "/signup", element: <SignupComponent /> },
      { path: "/logout", element: <LogoutComponent /> },
      ...(isDev()
        ? [
            {
              path: "/embed/applications/preview/*",
              element: <HardRedirect />,
            },
            {
              path: "/embed/applications/*",
              element: <HardRedirect />,
            },
          ]
        : []),
      {
        element: (
          <AdminDemoProvider>
            <MemoizedPrivateRoutes />
          </AdminDemoProvider>
        ),
        children: [
          { path: "custom-agents", lazy: lazyCustomAgents },
          { path: "custom-agents/new", lazy: lazyCustomAgents },
          { path: "custom-agents/:resourceId/edit", lazy: lazyCustomAgents },
          { path: "tools", lazy: lazyCustomAgents },
          { path: "tools/new", lazy: lazyCustomAgents },
          { path: "tools/:resourceId/edit", lazy: lazyCustomAgents },
          { path: "skills", lazy: lazyCustomAgents },
          { path: "skills/new", lazy: lazyCustomAgents },
          { path: "skills/:resourceId/edit", lazy: lazyCustomAgents },
          { path: "sessions/:sessionId", lazy: lazyCustomAgents },
          {
            path: "create",
            lazy: lazyCreateFromPrompt,
          },
          {
            path: "folders/:folderId/*",
            lazy: lazyHome,
          },
          {
            path: "integrations",
            lazy: lazyIntegrations,
          },
          {
            path: "profiles",
            lazy: lazyProfiles,
          },
          {
            path: "environments",
            lazy: lazyEnvironments,
          },
          {
            path: "data-setup",
            lazy: lazyDataSetup,
          },
          {
            path: "data-setup/instructions",
            lazy: lazyDataSetupInstructionPage,
          },
          {
            path: "databases",
            lazy: lazyNativeDatabases,
          },
          {
            path: "databases/provision",
            lazy: lazyNativeDatabases,
          },
          {
            path: "databases/provisioning/:provisioningId",
            lazy: lazyNativeDatabases,
          },
          {
            path: "databases/app/:applicationId",
            lazy: lazyNativeDatabases,
          },
          {
            path: "databases/database/:bindingId",
            lazy: lazyNativeDatabases,
          },
          {
            path: "databases/instance/:instanceId",
            lazy: lazyNativeDatabases,
          },
          {
            path: "repositories",
            lazy: lazyRepositories,
          },
          {
            path: "repositories/:repositoryId",
            lazy: lazyRepositoryDetails,
          },
          {
            path: `applications/${ApplicationRoute.Viewer}`,
            element: <HardRedirect />,
          },
          {
            path: "applications",
            element: <AccessModeRedirect />,
          },
          {
            path: "home?/*",
            lazy: lazyHome,
          },
          {
            path: "apps/*",
            lazy: lazyHome,
          },
          {
            path: "opas",
            lazy: lazyAgents,
          },
          {
            path: "opas/instructions",
            lazy: lazyAgentDeploymentInstructionPage,
          },
          {
            path: "access-tokens",
            lazy: lazyAccessTokensPage,
          },
          {
            path: "database",
            lazy: lazyDatabasePage,
          },
          {
            path: "external-auth",
            lazy: lazyExternalAuthPage,
          },
          {
            path: "external-experience",
            lazy: lazyExternalExperiencePage,
          },
          {
            path: "external-users",
            lazy: lazyExternalUsersPage,
          },
          {
            path: "overview",
            lazy: lazyOverviewPage,
          },
          {
            path: "platform-setup",
            lazy: lazyPlatformSetupPage,
          },
          {
            path: "workflows",
            lazy: lazyWorkflowsPage,
          },
          {
            path: "file-storage",
            lazy: lazyFileStoragePage,
          },
          {
            path: "identity",
            lazy: lazyIdentityPage,
          },
          {
            path: "app-analytics",
            lazy: lazyAppAnalyticsPage,
          },
          {
            path: "observability-monitor",
            lazy: lazyObservabilityMonitorPage,
          },
          {
            path: "platform-mcp",
            lazy: lazyPlatformMcpPage,
          },
          {
            path: "knowledge",
            lazy: lazyKnowledgeLayout,
            children: [
              {
                index: true,
                lazy: lazyKnowledgeList,
              },
              {
                path: "new",
                lazy: lazyKnowledgeCreate,
              },
              {
                path: ":factId",
                lazy: lazyKnowledgeDetail,
              },
            ],
          },
          {
            path: "users",
            lazy: lazyUsersPage,
          },
          {
            path: "users/:userId/:pageType",
            lazy: lazyUserDetail,
          },
          {
            path: "groups",
            lazy: lazyGroups,
          },
          {
            path: "groups/:groupId/:pageType",
            lazy: lazyGroupDetail,
          },
          {
            path: "roles-and-permissions",
            element: (
              <Navigate
                to={ROLES_AND_PERMISSIONS_URL(
                  RoleAndPermissionsPages.ORGANIZATION_ROLES,
                )}
                replace
              />
            ),
          },
          {
            path: ROLES_AND_PERMISSIONS_URL(),
            lazy: lazyRolesAndPermissions,
          },
          {
            path: "audit",
            lazy: lazyAuditLogs,
          },
          {
            path: "integrations/:pluginId/:datasourceId",
            lazy: lazyIntegrationForm.bind(null, IntegrationKind.PLUGIN),
          },
          {
            path: "integrations/:pluginId",
            lazy: lazyIntegrationForm.bind(null, IntegrationKind.PLUGIN),
          },
          {
            path: "workflows/:apiId/action?/:actionId?/*",
            element: <HardRedirect />,
          },
          {
            path: "workflows",
            element: <AccessModeRedirect />,
          },
          {
            path: "scheduled_jobs/:apiId/action?/:actionId?/*",
            element: <HardRedirect />,
          },
          {
            path: "scheduled_jobs",
            element: <AccessModeRedirect />,
          },
          {
            path: "oauth/github-callback",
            lazy: lazyGitHubOAuthCallback,
          },
          {
            path: "observability/:pageType?",
            lazy: lazyObservability,
          },
          {
            path: "secrets-management",
            lazy: lazySecretsManagement,
          },
          {
            path: "secrets-management/:pluginId/:datasourceId",
            lazy: lazyIntegrationForm.bind(null, IntegrationKind.SECRET),
          },
          {
            path: "secrets-management/:pluginId",
            lazy: lazyIntegrationForm.bind(null, IntegrationKind.SECRET),
          },
          {
            path: "general",
            lazy: lazyGeneralPage,
          },
          {
            path: "usage",
            lazy: lazyUsagePage,
          },
          {
            path: "ai-inference",
            lazy: lazyAiInferencePage,
          },
          {
            path: "custom-agent-models",
            lazy: lazyCustomAgentModels,
          },
          {
            path: "package-registry",
            lazy: lazyPackageRegistryPage,
          },
          {
            path: "spending",
            lazy: lazySpendingPage,
          },
          {
            path: "notifications",
            lazy: lazyInboxPage,
          },
          {
            path: "notifications/:id",
            lazy: lazyInboxPage,
          },
          {
            path: "personal-settings",
            lazy: lazyPersonalSettings,
          },
          {
            path: "settings/policy-gates",
            lazy: lazyPolicyGatesDemo,
          },
          {
            path: "settings/policy-gates/reset",
            lazy: lazyPolicyGatesDemoReset,
          },
          {
            path: "settings/policy-gates/:policyId",
            lazy: lazyPolicyGatesDemoDetail,
          },
          {
            path: "security-center-demo",
            lazy: lazySecurityCenterDemo,
          },
          {
            path: "security-center",
            lazy: lazySecurityCenter,
          },
          {
            path: "security-center/applications/:applicationId",
            lazy: lazySecurityCenterApplication,
          },
          {
            path: "security-center-demo/reviews/:reviewId",
            lazy: lazyCodeSecurityReview,
          },
          {
            path: "app-ai",
            lazy: lazyAppAiPage,
          },
          {
            path: "policy-agents",
            lazy: lazyPoliciesPage,
          },
          {
            path: "policy-agents/:policyId/runs/:runId",
            lazy: lazyPolicyRunReportPage,
          },
          {
            path: "policy-agents/:policyId",
            lazy: lazyPolicyRunsPage,
          },
          {
            path: "policy-agents/:policyId/edit",
            lazy: lazyPolicyAgentEditPage,
          },
          {
            path: "policies",
            loader: ({ request }) => {
              const url = new URL(request.url);
              return redirect(`${POLICIES_URL}${url.search}`);
            },
          },
          {
            path: "policies/:policyId/runs/:runId",
            loader: ({ params, request }) => {
              const url = new URL(request.url);
              return redirect(
                `${POLICIES_URL}/${params.policyId}/runs/${params.runId}${url.search}`,
              );
            },
          },
          {
            path: "policies/:policyId/edit",
            loader: ({ params, request }) => {
              const url = new URL(request.url);
              return redirect(
                `${POLICIES_URL}/${params.policyId}/edit${url.search}`,
              );
            },
          },
          {
            path: "policies/:policyId",
            loader: ({ params, request }) => {
              const url = new URL(request.url);
              return redirect(
                `${POLICIES_URL}/${params.policyId}${url.search}`,
              );
            },
          },
          {
            path: "policy-gates",
            loader: ({ request }) => {
              const url = new URL(request.url);
              return redirect(`${POLICIES_URL}${url.search}`);
            },
          },
          {
            path: "policy-gates/:policyId/runs/:runId",
            loader: ({ params, request }) => {
              const url = new URL(request.url);
              return redirect(
                `${POLICIES_URL}/${params.policyId}/runs/${params.runId}${url.search}`,
              );
            },
          },
          {
            path: "policy-gates/:policyId/edit",
            loader: ({ params, request }) => {
              const url = new URL(request.url);
              return redirect(
                `${POLICIES_URL}/${params.policyId}/edit${url.search}`,
              );
            },
          },
          {
            path: "policy-gates/:policyId",
            loader: ({ params, request }) => {
              const url = new URL(request.url);
              return redirect(
                `${POLICIES_URL}/${params.policyId}${url.search}`,
              );
            },
          },
          {
            path: "accept-invite",
            lazy: lazyAcceptInvite,
          },
          {
            path: "checkout",
            lazy: lazyCheckoutLoader,
          },
          {
            path: "checkout/complete",
            lazy: lazyCheckoutCompletePage,
          },
          {
            path: "admin/dbfs",
            lazy: lazyDBFSDownloadPage,
          },
          {
            path: OAUTH_CALLBACK_PATH,
            element: <OAuthImplicitCallback />,
          },
        ],
      },
      {
        path: "*",
        element: <PageNotFound />,
      },
    ],
  },
]);

const Router = () => {
  return (
    <Suspense fallback={loadingIndicator}>
      <RouterProvider router={router} fallbackElement={<FullPageSpinner />} />
    </Suspense>
  );
};

// Uses basic memoization because routes can't change
export default memo(Router);
